import React, { useEffect, useRef } from 'react'
import { MapContainer, TileLayer, Popup, Polygon, useMap } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import L from 'leaflet'
import 'leaflet.markercluster'

const getPolygonPointsFromMappedCoordinates = (coords) => {
  if (!coords) return []

  const pointKeys = Object.keys(coords)
    .filter(key => /^point[A-Z]$/.test(key))
    .sort((a, b) => a.charCodeAt(5) - b.charCodeAt(5))

  const points = []
  pointKeys.forEach(key => {
    const point = coords[key]
    if (!point) return

    const lat = parseFloat(point.latitude ?? point.lat)
    const lng = parseFloat(point.longitude ?? point.lng ?? point.lon)
    if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
      points.push([lat, lng])
    }
  })

  return points
}

const getFarmMarkerPosition = (farm) => {
  if (farm?.is_mapped && farm?.mapped_coordinates) {
    const points = getPolygonPointsFromMappedCoordinates(farm.mapped_coordinates)
    if (points.length > 0) {
      const lat = points.reduce((sum, [pointLat]) => sum + pointLat, 0) / points.length
      const lng = points.reduce((sum, [, pointLng]) => sum + pointLng, 0) / points.length
      return { lat, lng, isMapped: true }
    }
  }

  const lat = parseFloat(farm?.latitude)
  const lng = parseFloat(farm?.longitude)
  if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
    return { lat, lng, isMapped: false }
  }

  return null
}

const buildFarmPopupContent = (farm, isMapped) => `
  <div style="text-align: center; padding: 8px;">
    <div style="font-weight: bold; font-size: 14px; margin-bottom: 4px;">
      ${farm?.farm_name || 'Farm'}
    </div>
    <div style="font-size: 12px; color: #666; margin-bottom: 2px;">
      ${farm?.primary_crop || 'No crop specified'}
    </div>
    <div style="font-size: 11px; color: #888;">
      ${farm?.farm_size || 'Unknown'} acres
    </div>
    <div style="font-size: 11px; color: #888;">
      ${farm?.community || 'Unknown'}, ${farm?.district || 'Unknown'}
    </div>
    ${farm?.production_capacity ? `<div style="font-size: 11px; color: #22c55e; font-weight: 500; margin-top: 4px;">Capacity: ${farm.production_capacity} units</div>` : ''}
    <div style="font-size: 11px; color: ${isMapped ? '#2563eb' : '#f97316'}; font-weight: 500; margin-top: 4px;">
      ${isMapped ? '🗺️ Mapped boundary' : '📍 Not mapped'}
    </div>
  </div>
`

// Create larger custom icon for better visibility
const createLargeIcon = (isMapped = false, imageUrl = null) => {
  const iconSize = 40 // Larger size for better visibility
  const iconColor = isMapped ? '#FFB733' : '#8BC34A'
  const emoji = isMapped ? '🗺️' : '🌾'

  // Create a container div
  const container = document.createElement('div')
  container.style.cssText = `
    width: ${iconSize}px;
    height: ${iconSize}px;
    background-color: ${imageUrl ? 'transparent' : iconColor};
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${iconSize * 0.6}px;
    overflow: hidden;
  `

  if (imageUrl) {
    // Create image element
    const img = document.createElement('img')
    img.src = imageUrl
    img.alt = 'Farm'
    img.style.cssText = 'width: 100%; height: 100%; object-fit: cover; border-radius: 50%;'
    img.onerror = () => {
      // Fallback to emoji if image fails to load
      container.innerHTML = emoji
      container.style.backgroundColor = iconColor
    }
    container.appendChild(img)
  } else {
    container.textContent = emoji
  }

  return L.divIcon({
    className: 'custom-marker',
    html: container,
    iconSize: [iconSize, iconSize],
    iconAnchor: [iconSize / 2, iconSize / 2],
    popupAnchor: [0, -iconSize / 2]
  })
}

// Component to handle map panning when farm is selected
const MapController = ({ selectedFarmId, farms }) => {
  const map = useMap()

  useEffect(() => {
    if (selectedFarmId) {
      const farm = farms?.find(f => f?.id === selectedFarmId)
      if (farm) {
        // Check if farm has mapped coordinates (polygon)
        if (farm?.is_mapped && farm?.mapped_coordinates) {
          const points = getPolygonPointsFromMappedCoordinates(farm.mapped_coordinates)

          if (points.length > 0) {
            const bounds = L.latLngBounds(points)
            if (bounds.isValid()) {
              map.fitBounds(bounds, { padding: [100, 100], maxZoom: 22, animate: true, duration: 0.5 })
              return
            }
          }
        }

        // Use farm location if available
        if (farm?.latitude && farm?.longitude) {
          const lat = parseFloat(farm.latitude)
          const lng = parseFloat(farm.longitude)
          if (!isNaN(lat) && !isNaN(lng)) {
            // Zoom to reasonable level for a point
            map.setView([lat, lng], 18, { animate: true, duration: 0.5 })
          }
        }
      }
    }
  }, [selectedFarmId, farms, map])

  return null
}

const MapBoundsFitter = ({ farms }) => {
  const map = useMap()

  useEffect(() => {
    if (!farms?.length) return

    const allPoints = []
    farms.forEach(farm => {
      const markerPosition = getFarmMarkerPosition(farm)
      if (markerPosition) {
        allPoints.push([markerPosition.lat, markerPosition.lng])
      }
    })

    if (allPoints.length === 0) return

    const bounds = L.latLngBounds(allPoints)
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 })
    }
  }, [farms, map])

  return null
}

// Component to create and manage marker cluster group
const MarkerClusterGroup = ({ farms, onMarkerClick }) => {
  const map = useMap()
  const clusterGroupRef = useRef(null)

  useEffect(() => {
    if (!map) return

    // Create cluster group with custom styling
    const clusterGroup = L.markerClusterGroup({
      maxClusterRadius: 50, // Maximum radius that a cluster will cover
      spiderfyOnMaxZoom: true, // When you click a cluster at the bottom zoom level, it will spiderfy
      showCoverageOnHover: false, // Show a polygon when hovering over a cluster
      zoomToBoundsOnClick: true, // Zoom to bounds of cluster when clicked
      iconCreateFunction: function (cluster) {
        const count = cluster.getChildCount()
        let size = 'small'
        if (count > 100) size = 'large'
        else if (count > 20) size = 'medium'

        return L.divIcon({
          html: `<div style="
            background-color: #FFB733;
            color: white;
            border-radius: 50%;
            width: ${size === 'large' ? '50px' : size === 'medium' ? '45px' : '40px'};
            height: ${size === 'large' ? '50px' : size === 'medium' ? '45px' : '40px'};
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: ${size === 'large' ? '16px' : size === 'medium' ? '14px' : '12px'};
            border: 3px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          ">${count}</div>`,
          className: 'marker-cluster',
          iconSize: L.point(size === 'large' ? 50 : size === 'medium' ? 45 : 40, size === 'large' ? 50 : size === 'medium' ? 45 : 40)
        })
      }
    })

    // Add cluster group to map
    clusterGroupRef.current = clusterGroup
    map.addLayer(clusterGroup)

    // Cleanup function
    return () => {
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current)
        clusterGroupRef.current = null
      }
    }
  }, [map])

  // Add markers to cluster group
  useEffect(() => {
    if (!clusterGroupRef.current || !farms) return

    // Clear existing markers
    clusterGroupRef.current.clearLayers()

    // Collect all markers
    const markers = []

    farms.forEach(farm => {
      const markerPosition = getFarmMarkerPosition(farm)
      if (!markerPosition) return

      const farmImageUrl = farm?.images?.[0]?.image || null
      const marker = L.marker([markerPosition.lat, markerPosition.lng], {
        icon: createLargeIcon(markerPosition.isMapped, farmImageUrl)
      })

      marker.bindPopup(buildFarmPopupContent(farm, markerPosition.isMapped))
      marker.on('click', () => {
        if (onMarkerClick) {
          onMarkerClick(farm.id)
        }
      })

      markers.push(marker)
    })

    // Add all markers to cluster group
    if (markers.length > 0) {
      clusterGroupRef.current.addLayers(markers)
    }
  }, [farms, onMarkerClick])

  return null
}

const FarmLeafletMap = ({ farms, selectedFarmId, onMarkerClick }) => {
  const defaultCenter = [5.6037, -0.1870] // Set closer to your farm location
  const defaultZoom = 8 // Lower zoom for better overview
  const minZoom = 3 // Minimum zoom level to prevent white map
  const maxZoom = 25 // Maximum zoom level (Esri tiles typically available up to level 17)

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        minZoom={minZoom}
        maxZoom={maxZoom}
        style={{ height: '100%', width: '100%' }}
        maxBounds={[[-90, -180], [90, 180]]}
        maxBoundsViscosity={1.0}
        zoomControl={true}
        scrollWheelZoom={true}
        doubleClickZoom={true}
        touchZoom={true}
        boxZoom={true}
        keyboard={true}
      >
        {/* Primary tile layer - Esri World Imagery */}
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          attribution="&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
          maxZoom={maxZoom}
          minZoom={minZoom}
          maxNativeZoom={17}
          tileSize={256}
          zoomOffset={0}
          noWrap={false}
        />

        <MapController selectedFarmId={selectedFarmId} farms={farms} />
        <MapBoundsFitter farms={farms} />

        {/* Render polygons - always visible, not clustered */}
        {farms?.filter(farm => farm?.is_mapped && farm?.mapped_coordinates).map(farm => {
          const points = getPolygonPointsFromMappedCoordinates(farm.mapped_coordinates)
          const closedPoints = points.length >= 3 ? [...points, points[0]] : points

          return closedPoints.length >= 4 ? (
            <Polygon
              key={`polygon-${farm?.id}`}
              positions={closedPoints}
              pathOptions={{
                color: '#FFB733',
                fillColor: "rgba(255, 255, 159, 0.9)",
                fillOpacity: 0.4,
                weight: 2
              }}
            >
              <Popup>
                <div className="flex flex-col items-center p-2 bg-white rounded-lg shadow-lg">
                  <div className="w-16 h-16 rounded-full bg-[#fff3cd] flex items-center justify-center border-2 border-white shadow-md mb-2">
                    <span className="text-2xl">🗺️</span>
                  </div>
                  <div className="text-center">
                    <h3 className="font-semibold text-gray-800 text-sm">
                      {farm?.farm_name || 'Farm'}
                    </h3>
                    <p className="text-xs text-gray-600">
                      {farm?.primary_crop || 'No crop specified'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {farm?.farm_size || 'Unknown'} acres
                    </p>
                    <p className="text-xs text-gray-500">
                      {farm?.community || 'Unknown'}, {farm?.district || 'Unknown'}
                    </p>
                    {farm?.production_capacity && (
                      <p className="text-xs text-green-600 font-medium">
                        Capacity: {farm?.production_capacity} units
                      </p>
                    )}
                    <p className="text-xs text-blue-600 font-medium mt-1">
                      🗺️ Mapped boundary
                    </p>
                  </div>
                </div>
              </Popup>
            </Polygon>
          ) : null
        })}

        {/* Marker Cluster Group - handles all markers with clustering */}
        <MarkerClusterGroup farms={farms} onMarkerClick={onMarkerClick} />
      </MapContainer>
    </div>
  )
}

export default FarmLeafletMap
